import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { queryKeys, useAuthenticatedClient } from './queries'
import { useAuth } from '@/hooks/use-auth'
import type { components } from './schema'
import { parseUTCDate } from '@/lib/utils'

// Staff corrections to a product's store title, spec text, AI note, spec table, photos and spec
// sheets (USA-1004). A staff correction wins until it is undone.
export type ProductCorrections = components['schemas']['ProductCorrectionsRead']
export type ProductCorrectionsWrite = components['schemas']['ProductCorrectionsWrite']
export type TextCorrection = components['schemas']['TextCorrectionRead']
export type CorrectionRefresh = components['schemas']['CorrectionRefreshRead']
export type MediaCorrection = components['schemas']['ProductMediaCorrectionRead']
export type MediaKind = MediaCorrection['media_kind']
export type CorrectionField = ProductCorrectionsWrite['release'][number]

type ProductDetail = components['schemas']['DashboardProductDetailRead']
type ProductImage = components['schemas']['ProductImageRead']
type ProductDocument = components['schemas']['ProductDocumentRead']

// Base URL for the multipart upload, which does not go through openapi-fetch.
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:80'
// While the product rewrites its text and updates the store, check on it this often.
const REFRESH_POLL_MS = 5_000

export const productCorrectionKeys = {
  corrections: (productUuid: string) => ['products', 'corrections', productUuid] as const,
  media: (productUuid: string) => ['products', 'media-corrections', productUuid] as const,
}

export function refreshActive(refresh: CorrectionRefresh | null | undefined) {
  return refresh?.status === 'queued' || refresh?.status === 'running'
}

/** A photo or spec sheet as the product detail lists it. */
export type MediaLike = { uuid?: string | null; id?: number | null; hidden?: boolean | null }

/** The database id a photo or spec sheet is corrected by (null until the detail payload carries it). */
export function mediaIdOf(item: MediaLike): number | null {
  return typeof item.id === 'number' ? item.id : null
}

/** A message staff can act on, from an API error body or a thrown Error. */
export function correctionErrorMessage(error: unknown, fallback: string) {
  const detail = (error as { detail?: unknown } | null)?.detail
  if (typeof detail === 'string') return detail
  if (error instanceof Error && error.message) return error.message
  return fallback
}

/** "Max Weinzierl · 10/31/2026 · Summit sales confirmed." */
export function correctionDetail(
  by: string | null | undefined,
  at: string,
  reason?: string | null,
) {
  return [by || 'Staff', parseUTCDate(at).toLocaleDateString(), reason?.trim() || null]
    .filter(Boolean)
    .join(' · ')
}

function refreshProduct(queryClient: QueryClient, productUuid: string) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.products.detail(productUuid) })
}

export function useProductCorrections(productUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const key = productCorrectionKeys.corrections(productUuid)
  return useQuery({
    queryKey: key,
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/products/{uuid}/corrections', {
        params: { path: { uuid: productUuid } },
      })
      if (error) throw error
      // The rewrite just finished: the product's title, text and photos have changed.
      const before = queryClient.getQueryData<ProductCorrections>(key)
      if (refreshActive(before?.refresh) && !refreshActive(data.refresh)) {
        refreshProduct(queryClient, productUuid)
      }
      return data
    },
    refetchInterval: (query) => (refreshActive(query.state.data?.refresh) ? REFRESH_POLL_MS : false),
  })
}

/** Sets the given corrections and undoes the `release` ones; the response is the new state. */
export function useSaveProductCorrections(productUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (
      change: Omit<ProductCorrectionsWrite, 'release'> & { release?: CorrectionField[] },
    ) => {
      const client = await getClient()
      const { data, error } = await client.PUT('/dashboard/products/{uuid}/corrections', {
        params: { path: { uuid: productUuid } },
        body: { ...change, release: change.release ?? [] },
      })
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(productCorrectionKeys.corrections(productUuid), data)
      refreshProduct(queryClient, productUuid)
    },
  })
}

export function useProductMediaCorrections(productUuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: productCorrectionKeys.media(productUuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/products/{uuid}/media-corrections', {
        params: { path: { uuid: productUuid } },
      })
      if (error) throw error
      return data
    },
  })
}

export type MediaHiddenChange = {
  kind: MediaKind
  mediaId: number
  hidden: boolean
  reason?: string | null
}

function withHidden<T extends MediaLike>(
  item: T,
  mediaId: number,
  hidden: boolean,
): T {
  return mediaIdOf(item) === mediaId ? { ...item, hidden } : item
}

/** The product as it looks right after the change: the photo or spec sheet flips at once. */
function optimisticProduct(product: ProductDetail, change: MediaHiddenChange): ProductDetail {
  const { kind, mediaId, hidden } = change
  if (kind === 'image') {
    return {
      ...product,
      images: product.images.map((image: ProductImage) => withHidden(image, mediaId, hidden)),
    }
  }
  // A hidden spec sheet leaves the store list; a restored one returns to it.
  const all: ProductDocument[] = [...product.documents, ...product.other_documents]
  const moved = all.find((doc) => mediaIdOf(doc) === mediaId)
  if (!moved) return product
  const without = (docs: ProductDocument[]) => docs.filter((doc) => doc !== moved)
  const next = { ...moved, hidden }
  return hidden
    ? {
        ...product,
        documents: without(product.documents),
        other_documents: [...without(product.other_documents), next],
      }
    : {
        ...product,
        documents: [...without(product.documents), next],
        other_documents: without(product.other_documents),
      }
}

/** Hides or restores one photo or spec sheet; the page shows the change before the server answers. */
export function useSetProductMediaHidden(productUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const mediaKey = productCorrectionKeys.media(productUuid)
  const detailKey = queryKeys.products.detail(productUuid)
  return useMutation({
    mutationFn: async (change: MediaHiddenChange) => {
      const client = await getClient()
      const params = {
        params: {
          path: { uuid: productUuid, media_kind: change.kind, media_id: change.mediaId },
        },
        body: { reason: change.reason ?? null },
      }
      const { data, error } = change.hidden
        ? await client.POST('/dashboard/products/{uuid}/media/{media_kind}/{media_id}/hide', params)
        : await client.POST(
            '/dashboard/products/{uuid}/media/{media_kind}/{media_id}/restore',
            params,
          )
      if (error) throw error
      return data
    },
    onMutate: async (change) => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: mediaKey }),
        queryClient.cancelQueries({ queryKey: detailKey }),
      ])
      const previousMedia = queryClient.getQueryData<MediaCorrection[]>(mediaKey)
      const previousProduct = queryClient.getQueryData<ProductDetail | null>(detailKey)
      const optimistic: MediaCorrection = {
        id: -Date.now(),
        media_kind: change.kind,
        media_id: change.mediaId,
        action: change.hidden ? 'hide' : 'restore',
        reason: change.reason ?? null,
        user_name: null,
        created_at: new Date().toISOString(),
      }
      queryClient.setQueryData<MediaCorrection[]>(mediaKey, (rows) => [optimistic, ...(rows ?? [])])
      if (previousProduct) {
        queryClient.setQueryData(detailKey, optimisticProduct(previousProduct, change))
      }
      return { previousMedia, previousProduct }
    },
    onError: (_error, _change, context) => {
      if (!context) return
      queryClient.setQueryData(mediaKey, context.previousMedia)
      queryClient.setQueryData(detailKey, context.previousProduct)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: mediaKey })
      void queryClient.invalidateQueries({ queryKey: detailKey })
      void queryClient.invalidateQueries({
        queryKey: productCorrectionKeys.corrections(productUuid),
      })
    },
  })
}

export type MediaUpload = {
  kind: MediaKind
  file: File
  reason?: string | null
  /** A photo or spec sheet of the same kind that the new one replaces (it is hidden with it). */
  replacesId?: number | null
}

/** Adds a photo or spec sheet staff chose, optionally in place of an existing one. */
export function useUploadProductMedia(productUuid: string) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ kind, file, reason, replacesId }: MediaUpload) => {
      const token = await getAccessToken()
      if (!token) throw new Error('Not signed in')
      const form = new FormData()
      form.append('file', file)
      if (reason) form.append('reason', reason)
      if (replacesId != null) form.append('replaces_id', String(replacesId))
      const resp = await fetch(
        `${API_BASE_URL}/dashboard/products/${productUuid}/media/${kind}/upload`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form },
      )
      if (!resp.ok) {
        const body = (await resp.json().catch(() => null)) as { detail?: unknown } | null
        throw new Error(
          typeof body?.detail === 'string'
            ? body.detail
            : `Could not upload ${file.name}. Try again.`,
        )
      }
      return (await resp.json()) as components['schemas']['ProductMediaUploadRead']
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: productCorrectionKeys.media(productUuid) })
      void queryClient.invalidateQueries({
        queryKey: productCorrectionKeys.corrections(productUuid),
      })
      refreshProduct(queryClient, productUuid)
    },
  })
}

/**
 * The staff hide behind each hidden photo or spec sheet. Only a hidden item whose latest staff
 * change is a hide is staff-hidden (restorable); anything else hidden was hidden automatically.
 */
export function staffHiddenLookup(rows: MediaCorrection[] | undefined) {
  const latest = new Map<string, MediaCorrection>()
  // Rows come newest first, so the first one per photo or spec sheet is its staff state.
  for (const row of rows ?? []) {
    const key = `${row.media_kind}:${row.media_id}`
    if (!latest.has(key)) latest.set(key, row)
  }
  return (kind: MediaKind, item: MediaLike): MediaCorrection | null => {
    const id = mediaIdOf(item)
    if (id == null || !item.hidden) return null
    const row = latest.get(`${kind}:${id}`)
    return row?.action === 'hide' ? row : null
  }
}

/** The photos or spec sheets of one kind that staff hid and have not restored, newest first. */
export function staffHiddenRows(rows: MediaCorrection[] | undefined, kind: MediaKind) {
  const seen = new Set<number>()
  const hidden: MediaCorrection[] = []
  for (const row of rows ?? []) {
    if (row.media_kind !== kind || seen.has(row.media_id)) continue
    seen.add(row.media_id)
    if (row.action === 'hide') hidden.push(row)
  }
  return hidden
}

export function useStaffHidden(productId: string) {
  const { data } = useProductMediaCorrections(productId)
  return staffHiddenLookup(data)
}
